import {activateRegisterRecord} from './activate-register-record';
import './compact-workspace-register.css';
import {ResponsiveTable} from './ResponsiveTable';
import {Fragment,useState, type Key, type ReactNode} from 'react';
import {Icon} from './Icon';

export type DataTableColumn<Row> = Readonly<{
  id: string;
  header: ReactNode;
  render: (row: Row) => ReactNode;
  className?: string;
  align?: 'left' | 'right';
  /** Supply the canonical value to enable sorting; rendered text is never parsed. */
  sortValue?: (row: Row) => string | number | null | undefined;
}>;

export type DataTableProps<Row> = Readonly<{
  /** Compact collection rows; evidence matrices and editors keep their original layout. */
  compact?: boolean;
  caption: ReactNode;
  scrollLabel: string;
  rows: ReadonlyArray<Row>;
  rowHeader: DataTableColumn<Row>;
  columns: ReadonlyArray<DataTableColumn<Row>>;
  getRowKey: (row: Row) => Key;
  getRowClassName?: (row: Row) => string | undefined;
  getRowAttributes?: (row: Row) => Readonly<{id?:string;'data-source-key'?:string;'data-service-key'?:string}>;
  /** Expanded record facts span the table without distorting its column widths. */
  rowDetails?: (row: Row) => ReactNode;
  className?: string;
  footer?: ReactNode;
  pageSize?: number;
}>;

function classes(base: string, extra?: string) {
  return extra ? `${base} ${extra}` : base;
}

export function DataTable<Row>({
  compact=false,
  caption,
  scrollLabel,
  rows,
  rowHeader,
  columns,
  getRowKey,
  getRowClassName,
  getRowAttributes,
  rowDetails,
  className,
  footer,
  pageSize,
}: DataTableProps<Row>) {
  const [page,setPage]=useState(1);
  const [sort,setSort]=useState<{id:string;direction:'ascending'|'descending'}>();
  const allColumns=[rowHeader,...columns],selected=allColumns.find(column=>column.id===sort?.id);
  const sortedRows=selected?.sortValue&&sort?[...rows].sort((a,b)=>{
    const first=selected.sortValue!(a),second=selected.sortValue!(b);
    // Unknowns stay last in both directions; zero is a valid value.
    if(first==null)return second==null?0:1;if(second==null)return -1;
    const order=typeof first==='number'&&typeof second==='number'?first-second:String(first).localeCompare(String(second),'en-GB',{numeric:true});
    return sort.direction==='ascending'?order:-order;
  }):rows;
  const perPage=pageSize&&Number.isSafeInteger(pageSize)&&pageSize>0?pageSize:undefined;
  const pages=perPage?Math.max(1,Math.ceil(sortedRows.length/perPage)):1,currentPage=Math.min(page,pages);
  const visibleRows=perPage?sortedRows.slice((currentPage-1)*perPage,currentPage*perPage):sortedRows;
  const columnClass=(column:DataTableColumn<Row>)=>[column.className,column.align==='right'?'column-numeric':''].filter(Boolean).join(' ')||undefined;
  const heading=(column:DataTableColumn<Row>)=><th scope="col" className={columnClass(column)} key={column.id} aria-sort={sort?.id===column.id?sort.direction:column.sortValue?'none':undefined}>
    {column.sortValue?<button type="button" className="table-sort" onClick={()=>{setSort({id:column.id,direction:sort?.id===column.id&&sort.direction==='ascending'?'descending':'ascending'});setPage(1)}}>
      {column.header}<Icon name={sort?.id===column.id&&sort.direction==='descending'?'arrow-down':'arrow-up'}/>
    </button>:column.header}
  </th>;
  return <div
    className="data-table__scroller"
    role="region"
    aria-label={scrollLabel}
    tabIndex={0}
  >
    <ResponsiveTable className={classes('data-table'+(compact?' compact-workspace-register':''), className)}>
      <caption className="data-table__caption">{caption}</caption>
      <thead>
        <tr>
          {allColumns.map(heading)}
        </tr>
      </thead>
      <tbody>
        {visibleRows.map((row) => {
          const details=rowDetails?.(row);
          return <Fragment key={getRowKey(row)}><tr
          {...getRowAttributes?.(row)}
          className={classes('data-table__row', getRowClassName?.(row))}
          onClick={compact?activateRegisterRecord:undefined}
          onMouseOver={compact?event=>{const cell=event.target instanceof Element?event.target.closest<HTMLElement>('th,td'):null;if(cell)cell.title=cell.innerText.trim()}:undefined}
        >
          <th scope="row" className={columnClass(rowHeader)}>{rowHeader.render(row)}</th>
          {columns.map((column) => <td className={columnClass(column)} key={column.id}>{column.render(row)}</td>)}
        </tr>{details&&<tr className="data-table__detail-row"><td colSpan={allColumns.length}>{details}</td></tr>}</Fragment>;
        })}
      </tbody>
      {footer&&<tfoot>{footer}</tfoot>}
    </ResponsiveTable>
    {pages>1&&<nav className="pagination" aria-label={`${scrollLabel} pages`}><button type="button" className="secondary" disabled={currentPage===1} onClick={()=>setPage(currentPage-1)}>Previous</button><span>{currentPage} of {pages}</span><button type="button" className="secondary" disabled={currentPage===pages} onClick={()=>setPage(currentPage+1)}>Next</button></nav>}
  </div>;
}
